import { inject, Injectable } from "@angular/core";
import {
  arrayUnion,
  collection,
  deleteField,
  doc,
  DocumentReference,
  getDoc,
  getDocs,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where
} from "firebase/firestore";
import { docData, Firestore, Timestamp } from "@angular/fire/firestore";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { v4 as uuidV4 } from "uuid";
import {
  cloudFunctionNames,
  ICustomEmailContent,
  IEntity,
  IEntityOwner,
  IEntityOwnerFieldsToDelete,
  INewOwner,
  IOwner,
  IPersonOwner,
  IPersonOwnerFieldsToDelete,
  isPersonOwner,
  PaginatedData,
  UUID,
} from "src/app/core/interfaces";
import { convertDatesToTimestamps, convertTimestampsToDates, handleLoadingState } from "src/app/core/utils/helper-functions";
import {
  IEntityOwnerDTO,
  IFileSendSingleAutoCollectEmailReq,
  IPersonOwnerDTO,
  IProSendSingleAutoCollectEmailReq,
  ISendMultipleAutoCollectEmailsReq,
  ISendMultipleAutoCollectEmailsRes,
  ISendSingleAutoCollectEmailReq,
  ISendSingleAutoCollectEmailRes
}  from "@cloud-functions/interfaces";
import {
  CombinedOwnerFields,
  GetAllOwnersRes,
  GetOwnerReq,
  GetOwnerRes,
  GetOwnersByEntityIdReq,
  GetOwnersByEntityIdRes,
  GetOwnersPaginatedRes,
  IEntityOwnerCf,
  IOwnerCf,
  IPersonOwnerCf,
  isEntityOwnerCf,
  isPersonOwnerCf,
  PaginatedDataReq,
  RemoveEntityFromOwnerReq,
  RemoveEntityFromOwnerRes,
  RemoveOrgEntitiesFromOwnerReq,
  SearchOwnersReq,
  SearchOwnersRes,
  UploadGovIdImageByOwnerIdReq,
  UploadGovIdImageByTokenReq,
  UploadGovIdImageReq,
  UploadGovIdImageRes
} from "@cloud-functions/interfaces";
import { ref } from "firebase/storage";
import { deleteObject, Storage } from "@angular/fire/storage";
import { debounceTime, filter, map, merge, Observable, switchMap, withLatestFrom } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class OwnerService {
  private firestore: Firestore = inject(Firestore);
  private functions: Functions = inject(Functions);
  private storage: Storage = inject(Storage);

  private ownersCollection = collection(this.firestore, "owners");

  private readonly personOwnerFieldsToDelete: ReadonlyArray<keyof IPersonOwnerFieldsToDelete> = [
    "middleName",
    "suffix",
    "hasFincenId",
    "autoCollectId",
    "dob",
    "displayDob",
    "jurisdictionIssuer",
    "issuerState",
    "issuerTribe",
    "issuerOtherTribe",
    "docType",
    "docIdNum",
    "addressType",
    "address",
    "minor",
  ];

  private readonly entityOwnerFieldsToDelete: ReadonlyArray<keyof IEntityOwnerFieldsToDelete> = [
    "entityLegalName",
    "isExemptEntity",
    "isSignatoryEntity",
  ];

  private readonly personOwnerFieldsToDeleteMethods = Object.fromEntries(
    this.personOwnerFieldsToDelete.map(key => [key, deleteField()])
  ) as Record<keyof IPersonOwnerFieldsToDelete, ReturnType<typeof deleteField>>;

  private readonly entityOwnerFieldsToDeleteMethods = Object.fromEntries(
    this.entityOwnerFieldsToDelete.map(key => [key, deleteField()])
  ) as Record<keyof IEntityOwnerFieldsToDelete, ReturnType<typeof deleteField>>;

  private getOwnerRef(ownerId: UUID): DocumentReference<IOwnerCf> {
    return doc(
      this.firestore,
      `owners/${ownerId}`,
    ) as DocumentReference<IOwnerCf>;
  }

  public getEntityOwnersFirestoreObservable(
    entity$: Observable<IEntity>,
    ownerUpdateFilter$: Observable<boolean>
  ): Observable<IOwnerCf | null> {
    const ownerListeners = (entity: IEntity): Observable<IOwnerCf | undefined> => {
      const ownerListeners = Object.keys(entity.ownerRelationships)
        .map(ownerId => this.getOwnerRef(ownerId))
        .map(ownerRef => docData(ownerRef));
      return merge(...ownerListeners);
    }

    return entity$.pipe(
      switchMap(ownerListeners),
      withLatestFrom(ownerUpdateFilter$.pipe(
        switchMap(handleLoadingState),
      )),
      filter(([/* ownerCf */, isOwnerUpdating]) => !isOwnerUpdating),
      debounceTime(1000),
      map(([ownerCf]) => ownerCf ?? null),
    )
  }

  public static hasUploadedGovId = (owner: IOwner | null): owner is IPersonOwner & { docUrl: string } => {
    return !!owner && isPersonOwner(owner) && !!owner.docUrl;
  };

  /**
   * Converts an owner object to the Cloud Firestore format, excluding `entityIdsMap`.
   * Used for creating and updating existing owners
   *
   * @param {Partial<IOwner>} owner - The owner object to convert.
   *
   * @return {Partial<Omit<IOwnerCf, "entityIdsMap">>} The converted owner object.
   */
  private convertOwnerToCf(owner: Partial<IOwner>): Partial<Omit<IOwnerCf, "entityIdsMap">> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ownerCf = convertDatesToTimestamps<any>(owner);

    delete ownerCf.entityIds;
    delete ownerCf.groupIds;
    delete ownerCf.userIds;

    return ownerCf;
  }

  /**
   * Removes fields restricted to change by update method from the owner object.
   *
   * @param {Partial<IOwnerCf>} owner - The owner object to process.
   *
   * @return {Partial<IOwnerCf>} The owner object without restricted fields.
   */
  private removeRestrictedToUpdateFields(owner: Partial<IOwnerCf>): Partial<IOwnerCf> {
    if (!owner.isEntity) {
      const personalOwner = owner as Partial<IPersonOwnerCf>;
      const restrictedFields = [
        "lastIdCollectionEmailSent",
        "countOfIdCollectionEmailsSent",
        "docUrl",
        "originalAttachmentFileName",
      ];

      for (const field of restrictedFields) {
        delete personalOwner[field as keyof IPersonOwnerCf];
      }

      return personalOwner;
    }

    return owner;
  }


  /**
   * Retrieves all owners available to current user using Cloud Functions.
   *
   * @return {Promise<IOwner[]>} A promise that resolves to a list of all available owners.
   *
   * @throws {Error} If the callable function fails.
   */
  public async getAll(): Promise<IOwner[]> {
    const getAllOwnersCallable = httpsCallable<
      void,
      GetAllOwnersRes
    >(this.functions, cloudFunctionNames.owner.getAllOwners);

    const getAllOwnersResponse = await getAllOwnersCallable();

    if (!getAllOwnersResponse.data.success) {
      throw new Error(getAllOwnersResponse.data.message);
    }

    return getAllOwnersResponse.data.owners.map(owner => convertTimestampsToDates(owner));
  }

  public async getPersonOwnersPaginated(
    payload: PaginatedDataReq<IPersonOwner, CombinedOwnerFields>
  ): Promise<PaginatedData<IPersonOwner>> {
    const getPersonOwnersPaginatedCallable = httpsCallable<
      PaginatedDataReq<IPersonOwner, CombinedOwnerFields>,
      GetOwnersPaginatedRes<IPersonOwnerDTO>
    >(this.functions, cloudFunctionNames.owner.getPersonOwnersPaginated);

    const getPersonOwnersPaginatedResponse = await getPersonOwnersPaginatedCallable(payload);

    if (!getPersonOwnersPaginatedResponse.data.success) {
      throw new Error(getPersonOwnersPaginatedResponse.data.message);
    }

    const paginatedData = getPersonOwnersPaginatedResponse.data.data;
    const personOwners = paginatedData.data.map(owner => {
      return convertTimestampsToDates(owner) as IPersonOwner;
    });
    return {
      ...paginatedData,
      data: personOwners,
    };
  }

  public async getEntityOwnersPaginated(payload: PaginatedDataReq<IEntityOwner>): Promise<PaginatedData<IEntityOwner>> {
    const getEntityOwnersPaginatedCallable = httpsCallable<
      PaginatedDataReq<IEntityOwner, CombinedOwnerFields>,
      GetOwnersPaginatedRes<IEntityOwnerDTO>
    >(this.functions, cloudFunctionNames.owner.getEntityOwnersPaginated);

    const getEntityOwnersPaginatedResponse = await getEntityOwnersPaginatedCallable(payload);

    if (!getEntityOwnersPaginatedResponse.data.success) {
      throw new Error(getEntityOwnersPaginatedResponse.data.message);
    }

    const paginatedData = getEntityOwnersPaginatedResponse.data.data;
    const personOwners = paginatedData.data.map(owner => {
      return convertTimestampsToDates(owner) as IEntityOwner;
    });
    return {
      ...paginatedData,
      data: personOwners,
    };
  }


  /**
   * Retrieves an owner by its ID using Cloud Functions.
   *
   * @param {UUID} ownerId - The ID of the owner.
   * @param [isNewlyCreated=false] - A flag indicating if the owner was newly created,
   * allowing bypass of entity count checks.
   *
   * @return {Promise<IOwner>} A promise that resolves to the owner
   *
   * @throws {Error} If the callable function fails or owner not found.
   */
  public async getById(ownerId: UUID, isNewlyCreated = false): Promise<IOwner> {
    const getOwnerCallable = httpsCallable<
      GetOwnerReq,
      GetOwnerRes
    >(this.functions, cloudFunctionNames.owner.getOwner);

    const getOwnerResponse = await getOwnerCallable(
      {ownerId, isNewlyCreated}
    );

    if (!getOwnerResponse.data.success) {
      throw new Error(getOwnerResponse.data.message);
    }

    return convertTimestampsToDates(getOwnerResponse.data.owner);
  }

  /**
   * Retrieves owners by an entity ID.
   *
   * @param {UUID} entityId - The ID of the entity.
   *
   * @return {Promise<IOwner[]>} A promise that resolves to a list of owners.
   *
   * @throws {Error} If the callable function fails.
   */
  public async getByEntityId(entityId: UUID, orgId?: UUID): Promise<IOwner[]> {
    const getOwnersByEntityIdCallable = httpsCallable<
      GetOwnersByEntityIdReq,
      GetOwnersByEntityIdRes
    >(this.functions, cloudFunctionNames.owner.getOwnersByEntityId);

    const getOwnersByEntityIdResponse = await getOwnersByEntityIdCallable({ entityId, orgId });

    if (!getOwnersByEntityIdResponse.data.success) {
      throw new Error(getOwnersByEntityIdResponse.data.message);
    }

    return getOwnersByEntityIdResponse.data.owners.map(convertTimestampsToDates);
  }

  /**
   * Searches for available owners matching the given owner data.
   *
   * @param {IOwner} owner - The owner data to search for.
   *
   * @return {Promise<IOwner[]>} A promise that resolves to a list of matching owners.
   *
   * @throws {Error} If the callable function fails.
   */
  public async search(owner: IOwner): Promise<IOwner[]> {
    const searchOwnersCallable = httpsCallable<
      SearchOwnersReq,
      SearchOwnersRes
    >(this.functions, cloudFunctionNames.owner.searchOwners);

    const searchOwnersResponse = await searchOwnersCallable({owner});

    if (!searchOwnersResponse.data.success) {
      throw new Error(searchOwnersResponse.data.message);
    }

    return convertTimestampsToDates(searchOwnersResponse.data.owners);
  }

  /**
   * Creates a new owner.
   *
   * @param {IOwner | INewOwner<IOwner>} owner - The owner data to create.
   *
   * @returns {Promise<IOwner>} A promise that resolves to the created owner.
   *
   * @throws {Error} If the creation fails.
   */
   public async create(owner: IOwner | INewOwner<IOwner>, userId: UUID): Promise<IOwner> {
    const ownerId = ("id" in owner && owner.id) ? owner.id : uuidV4();

    const ownerRef = doc(this.ownersCollection, ownerId);

    const ownerCf = this.convertOwnerToCf(owner);

    const ownerToCreate = owner.isEntity
      ? <IEntityOwnerCf>{
        ...this.removeRestrictedToUpdateFields(ownerCf),
        entityIdsMap: {},
        id: ownerId,
        lastModifiedBy: userId,
      } : <IPersonOwnerCf>{
        ...this.removeRestrictedToUpdateFields(ownerCf),
        lastIdCollectionEmailSent: null,
        countOfIdCollectionEmailsSent: 0,
        docUrl: null,
        originalAttachmentFileName: null,
        entityIdsMap: {},
        id: ownerId,
        lastModifiedBy: userId,
      }

    await setDoc(ownerRef, ownerToCreate);

    return this.getById(ownerId, true);
  }


  /**
   * Updates an existing owner.
   *
   * @param {Partial<IOwner>} owner - The partial owner data to update.
   *
   * @returns {Promise<IOwner>} A promise that resolves to the updated owner.
   *
   * @throws {Error} If the update fails.
   */
  public async update(owner: IOwner, userId: UUID): Promise<IOwner> {
    if (!owner?.id) {
      throw new Error("Id of owner to update is undefined");
    }

    const ownerRef = doc(this.ownersCollection, owner.id);
    await runTransaction(this.firestore, async (transaction) => {
      const ownerSnap = await transaction.get(ownerRef);

      if (!ownerSnap.exists()) {
        throw new Error("Owner to update doesn't exist");
      }

      const existingOwner = ownerSnap.data() as IOwnerCf;

      let ownerFieldsToDelete: Record<string, ReturnType<typeof deleteField>> = {};
      if (owner.isEntity !== existingOwner.isEntity) {
        if (isPersonOwnerCf(existingOwner)) {
          ownerFieldsToDelete = this.personOwnerFieldsToDeleteMethods
        } else if (isEntityOwnerCf(existingOwner)) {
          ownerFieldsToDelete = this.entityOwnerFieldsToDeleteMethods
        }
      }

      const ownerCf = this.convertOwnerToCf(owner);
      const ownerToUpdate = {
        ...this.removeRestrictedToUpdateFields(ownerCf),
        ...ownerFieldsToDelete,
        lastModifiedDate: Timestamp.now(),
        lastModifiedBy: userId,
      };

      transaction.update(ownerRef, ownerToUpdate);
    });

    return this.getById(owner.id);
  }

  /**
   * Adds an entity to an owner.
   *
   * @param {UUID} ownerId - The ID of the owner.
   * @param {UUID} entityId - The ID of the entity to add.
   * @param {UUID} [orgId] - The organization ID entity belongs to. Required for PRO and handed off entities.
   *
   * @returns {Promise<IOwner>} A promise that resolves to the updated owner.
   *
   * @throws {Error} If the update fails.
   */
  public async addEntity(ownerId: UUID, userId: UUID, entityId: UUID, orgId?: UUID): Promise<IOwner> {
    if (!ownerId) {
      throw new Error("Owner Id is undefined");
    }
    if (!entityId) {
      throw new Error("Id of entity to add is undefined");
    }

    const ownerRef = doc(this.ownersCollection, ownerId);
    const ownerSnap = await getDoc(ownerRef);

    if (!ownerSnap.exists()) {
      throw new Error("Owner to update doesn't exist");
    }

    await updateDoc(ownerRef, {
      [`entityIdsMap.${orgId || "secureFile"}`]: arrayUnion(entityId),
      lastModifiedDate: Timestamp.now(),
      lastModifiedBy: userId,
    });

    return this.getById(ownerId);
  }

  /**
   * Removes an entity from an owner.
   *
   * @param {UUID} ownerId - The ID of the owner.
   * @param {UUID} entityId - The ID of the entity to remove.
   * @param {UUID | null} orgId - The organization ID entity belongs to. Required for PRO and handed off entities.
   *
   * @returns {Promise<IOwner | null>} A promise that resolves to the updated owner or
   * null if has been deleted or unavailable.
   *
   * @throws {Error} If the removal fails.
   */
  public async removeEntity(ownerId: UUID, entityId: UUID, orgId: UUID | null): Promise<IOwner | null> {
    if (!ownerId) {
      throw new Error("Owner Id is undefined");
    }
    if (!entityId) {
      throw new Error("Id of entity to remove is undefined");
    }

    const removeEntityFromOwnerCallable = httpsCallable<
      RemoveEntityFromOwnerReq,
      RemoveEntityFromOwnerRes
    >(this.functions, cloudFunctionNames.owner.removeEntityFromOwner);

    const removeEntityFromOwnerResponse = await removeEntityFromOwnerCallable({ownerId, entityId, orgId});

    if (!removeEntityFromOwnerResponse.data.success) {
      throw new Error(removeEntityFromOwnerResponse.data.message);
    }

    const updatedOwner = removeEntityFromOwnerResponse.data.updatedOwner;

    if (updatedOwner) {
      // Entity has been removed
      return convertTimestampsToDates(updatedOwner);
    } else {
      // The owner is no longer available for this user
      return null;
    }
  }


  /**
   * Removes all organization entities from an owner.
   *
   * @param {UUID} ownerId - The ID of the owner.
   * @param {UUID} orgId - The ID of the organization.
   *
   * @returns {Promise<IOwner | null>} A promise that resolves to the updated owner or
   * null if has been deleted or unavailable.
   *
   * @throws {Error} If the removal fails.
   */
  public async removeOrgEntities(ownerId: UUID, orgId: UUID): Promise<IOwner | null> {
    if (!ownerId) {
      throw new Error("Owner Id is undefined");
    }
    if (!orgId) {
      throw new Error("Id of entity to remove is undefined");
    }

    const removeOrgEntitiesFromOwnerCallable = httpsCallable<
      RemoveOrgEntitiesFromOwnerReq,
      RemoveEntityFromOwnerRes
    >(this.functions, cloudFunctionNames.owner.removeEntityFromOwner);

    const removeOrgEntitiesFromOwnerResponse = await removeOrgEntitiesFromOwnerCallable(
      {ownerId, orgId}
    );

    if (!removeOrgEntitiesFromOwnerResponse.data.success) {
      throw new Error(removeOrgEntitiesFromOwnerResponse.data.message);
    }

    const updatedOwner = removeOrgEntitiesFromOwnerResponse.data.updatedOwner;

    if (updatedOwner) {
      // Entities has been removed
      return convertTimestampsToDates(updatedOwner);
    } else {
      // The owner is no longer available for this user
      return null;
    }
  }

  /**
   * Removes an entity from multiple owners.
   *
   * @param {UUID[]} ownerIds - An array of owner IDs from which the entity will be removed.
   * @param {UUID} entityId - The ID of the entity to remove.
   * @param {UUID | null} orgId - The organization ID the entity belongs to. Required for PRO and handed-off entities.
   *
   * @return {Promise<{[ownerId: UUID]: IOwner | null}>} A promise that resolves to a map where the key is the owner ID
   * and the value is the updated owner object or `null` if the owner is no longer available.
   *
   * @throws {Error} If the entity ID is undefined, an owner ID is undefined, or the removal fails for any owner.
   */
  public async removeEntityFromOwners(ownerIds: UUID[], entityId: UUID, orgId: UUID | null): Promise<{
    [ownerId: UUID]: IOwner | null
  }> {
    if (!entityId) {
      throw new Error("Id of entity to remove is undefined");
    }

    const updatedOwnersMap: { [ownerId: UUID]: IOwner | null } = {};

    await Promise.all(ownerIds.map(async ownerId => {
      if (!ownerId) {
        throw new Error("Owner Id is undefined");
      }

      const removeEntityFromOwnerCallable = httpsCallable<
        RemoveEntityFromOwnerReq,
        RemoveEntityFromOwnerRes
      >(this.functions, cloudFunctionNames.owner.removeEntityFromOwner);

      const removeEntityFromOwnerResponse = await removeEntityFromOwnerCallable({ownerId, entityId, orgId});

      if (!removeEntityFromOwnerResponse.data.success) {
        throw new Error(removeEntityFromOwnerResponse.data.message);
      }

      updatedOwnersMap[ownerId] = convertTimestampsToDates(removeEntityFromOwnerResponse.data.updatedOwner);
    }))

    return updatedOwnersMap;
  }

  private async getOwnerEntityNames(ownerId: UUID, orgId: UUID): Promise<string> {
    const entitiesQuery = query(
      collection(this.firestore, "organization", orgId, "entityList"),
      where(`ownerRelationships.${ownerId}`, "!=", null)
    );

    const snapshot = await getDocs(entitiesQuery);
    return snapshot.docs
      .map(doc => doc.data() as IEntity)
      .map(entity => entity.legalName)
      .join(", ");
  }

  public async proSendSingleAutoCollectEmail(
    owner: IPersonOwner,
    senderName: string,
    orgId: UUID,
    customEmailContent?: ICustomEmailContent
  ): Promise<IOwner> {
    if (!owner.id) {
      throw new Error("No owner ID found. Cannot send auto collect email.");
    }
    if (!owner.email) {
      throw new Error("No owner email found. Cannot send auto collect email.");
    }

    const companyName = await this.getOwnerEntityNames(owner.id, orgId);

    const payload: IProSendSingleAutoCollectEmailReq = {
      userType: "secure-pro",
      email: owner.email,
      name: `${owner.firstName} ${owner.lastName}`,
      ownerId: owner.id,
      orgId,
      senderName,
      companyName,
      ...customEmailContent && { customEmailContent },
    }

    return this.sendSingleAutoCollectEmail(payload);
  }

  public fileSendSingleAutoCollectEmail(
    owner: IPersonOwner,
    entityLegalName: string,
    entityId: UUID,
  ): Promise<IOwner> {
    if (!owner.id) {
      throw new Error("No owner ID found. Cannot send auto collect email.");
    }
    if (!owner.email) {
      throw new Error("No owner email found. Cannot send auto collect email.");
    }

    const payload: IFileSendSingleAutoCollectEmailReq = {
      userType: "secure-file",
      email: owner.email,
      name: `${owner.firstName} ${owner.lastName}`,
      ownerId: owner.id,
      senderName: entityLegalName,
      companyName: entityLegalName,
      entityId,
    }

    return this.sendSingleAutoCollectEmail(payload);
  }

  private async sendSingleAutoCollectEmail(payload: ISendSingleAutoCollectEmailReq): Promise<IOwner> {
    const sendSingleAutoCollectEmailCallable = httpsCallable<
      ISendSingleAutoCollectEmailReq,
      ISendSingleAutoCollectEmailRes
    >(this.functions, cloudFunctionNames.imageCollection.sendSingleAutoCollectEmail);

    const sendAutoCollectEmailResponse = await sendSingleAutoCollectEmailCallable(payload);

    if (!sendAutoCollectEmailResponse.data.success) {
      throw new Error(sendAutoCollectEmailResponse.data.message);
    }

    return convertTimestampsToDates(sendAutoCollectEmailResponse.data.updatedOwner);
  }

  /**
   * Uploads a government ID image.
   *
   * @param {UploadGovIdImageReq} requestData - The request data containing the image, media type,
   * and owner or auto-collect token information.
   *
   * @return {Promise<void>} A promise that resolves when the image is successfully uploaded.
   *
   * @throws {Error} If the media type is invalid or the upload fails.
   */
  private async uploadGovIdImage(requestData: UploadGovIdImageReq): Promise<void> {
    if (
      !["jpeg", "jpg", "png", "pdf"].some((mediaType) =>
        requestData.mediaType.includes(mediaType)
      )
    ) {
      throw new Error("Invalid media type");
    }

    const uploadGovIdImageCallable = httpsCallable<
      UploadGovIdImageReq,
      UploadGovIdImageRes
    >(this.functions, cloudFunctionNames.owner.uploadGovIdImage);

    const uploadGovIdImageResponse = await uploadGovIdImageCallable(
      requestData
    );
    if (!uploadGovIdImageResponse.data.success) {
      throw new Error(uploadGovIdImageResponse.data.message);
    }
  }

  /**
   * Uploads a government ID image using an auto-collect token.
   *
   * @param {string} image - The base64-encoded image string to upload.
   * @param {string} mediaType - The MIME type of the image (e.g., "image/jpeg", "application/pdf").
   * @param {string} token - The auto-collect token.
   *
   * @return {Promise<void>} A promise that resolves when the image is successfully uploaded.
   *
   * @throws {Error} If the media type is invalid or the upload fails.
   */
  public async uploadGovIdImageByToken(image: string, mediaType: string, token: string): Promise<void> {
    const requestData: UploadGovIdImageByTokenReq = {
      image,
      mediaType,
      token,
    };

    await this.uploadGovIdImage(requestData);
  }

  /**
   * Uploads a government ID image for a specific owner.
   *
   * @param {string} image - The base64-encoded image string to upload.
   * @param {string} mediaType - The MIME type of the image (e.g., "image/jpeg", "application/pdf").
   * @param {UUID} ownerId - The unique identifier of the owner.
   *
   * @return {Promise<void>} A promise that resolves with the updated owner object when the image is successfully uploaded.
   *
   * @throws {Error} If the media type is invalid or the upload fails.
   */
  public async uploadGovIdImageByOwnerId(image: string, mediaType: string, ownerId: UUID): Promise<IOwner> {
    const requestData: UploadGovIdImageByOwnerIdReq = {
      image,
      mediaType,
      ownerId,
    };

    await this.uploadGovIdImage(requestData);

    return this.getById(ownerId);
  }

  /**
   * Replaces an existing government ID image for a specific owner.
   *
   * @param {UUID} ownerId - The unique identifier of the owner.
   * @param {string} image - The base64-encoded new image string to upload.
   * @param {string} mediaType - The MIME type of the new image (e.g., "image/jpeg", "application/pdf").
   *
   * @return {Promise<void>} A promise that resolves with the updated owner object when the image is successfully replaced.
   *
   * @throws {Error} If the replacement process fails.
   */
  public async replaceGovId(image: string, mediaType: string, ownerId: UUID, docUrl: string | null): Promise<IOwner> {
    await this.deleteGovId(ownerId, docUrl, false);
    await this.uploadGovIdImageByOwnerId(image, mediaType, ownerId);

    return this.getById(ownerId);
  }

  /**
   * Deletes the government ID image associated with a specific owner.
   *
   * @param {UUID} ownerId - The unique identifier of the owner whose government ID image will be deleted.
   * @param {boolean} [updateOwnerDoc=true] - Whether to update the owner document after deletion.
   *
   * @return {Promise<IOwner>} A promise that resolves with the updated owner object after deletion.
   *
   * @throws {Error} If the owner ID is undefined, or if the deletion process fails due to an undefined storage reference.
   */
  public async deleteGovId(ownerId: UUID, docUrl: string | null, updateOwnerDoc = true): Promise<IOwner> {
    if (!ownerId.trim()) {
      throw new Error("Owner to delete ID is undefined");
    }
    const ownerDocUrl = docUrl ?? `owner.-gov-ids/${ownerId}`;

    const storageRef = ref(this.storage, ownerDocUrl);

    if (!storageRef) {
      throw new Error("StorageRef is not defined");
    }

    await deleteObject(storageRef);

    if (updateOwnerDoc) {
      const ownerRef = doc(this.ownersCollection, ownerId);

      await updateDoc(ownerRef, {
        docUrl: null,
        lastModifiedDate: Timestamp.now(),
      });
    }

    return this.getById(ownerId);
  }

  /**
   * Sends multiple emails to collect the ID of the specified owners.
   *
   * @param {UUID[]} ownerIds - An array of owner IDs to which the emails will be sent.
   * @param {UUID} orgId - The organization ID for which the emails are sent.
   * @param {string} [entityName] - An optional entity name to be included in the email content.
   *
   * @return {Promise<IOwner[]>} A promise that resolves to an array of updated owner objects.
   *
   * @throws {Error} Throws an error if the email sending fails.
   */
  public async sendMultipleAutoCollectEmails(
    ownerIds: UUID[],
    orgId: UUID,
    entityName?: string
  ): Promise<IOwner[]> {
    const sendMultipleAutoCollectEmailsCallable = httpsCallable<
      ISendMultipleAutoCollectEmailsReq,
      ISendMultipleAutoCollectEmailsRes
    >(this.functions, cloudFunctionNames.imageCollection.sendMultipleAutoCollectEmails);

    const sendMultipleAutoCollectEmailsResponse = await sendMultipleAutoCollectEmailsCallable({
        orgId,
        entityName,
        ownerIds,
      });

    if (!sendMultipleAutoCollectEmailsResponse.data.success) {
      throw new Error(sendMultipleAutoCollectEmailsResponse.data.message);
    }

    return convertTimestampsToDates(sendMultipleAutoCollectEmailsResponse.data.owners);
  }
}

